import React, { Suspense, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { LoadingModal } from '@app/components/Modal';
import { useCanChangePlan, useCanPlaceOrders } from '@app/hooks';
import { selectLocale, selectPlanInfo } from '@app/redux';

import { useModalContext } from './utils/hooks/useDialogType';

import styles from './styles.module.scss';

const BuyMore = React.lazy(() => import('./BuyMoreModal'));

export const AddDataBoostSection = () => {
  const { t } = useTranslation('Usage');

  const { dialogType, dialogTypeHandlers } = useModalContext();
  const { resetDialogType, setBuyMoreDialog } = dialogTypeHandlers;

  const handleAddDataBoost = useCallback(() => {
    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_START,
      event_name: DataLayerEventName.FORM_START,
      event_label: `${AnalyticsEventNames.BuyMoreInitiated}`,
      event_category: Categories.BuyMore,
    });

    setBuyMoreDialog();
  }, [setBuyMoreDialog]);

  const { localeFetched } = useSelector(selectLocale);
  const {
    data: {
      characteristics: { isBuyMoreAvailable },
    },
  } = useSelector(selectPlanInfo);
  const { canPlaceOrders } = useCanPlaceOrders();
  const canChangePlan = useCanChangePlan();
  const isButtonDisabled = (!canChangePlan || !isBuyMoreAvailable || !canPlaceOrders) ?? false;

  const textColor = isButtonDisabled ? 'disabled' : 'regular';

  return (
    <div data-cy="buy-more-container" className={styles['buyMoreContainer']}>
      <Txt mr="24px" variant="smallRegular" color={textColor}>
        {t('buyMore.text')}
      </Txt>
      <Button
        data-cy="buy-more-button"
        variant="secondary"
        theme={'light'}
        buttonSize="small"
        onClick={handleAddDataBoost}
        disabled={isButtonDisabled}
        style={{ width: 160 }}
      >
        <Txt style={{ whiteSpace: 'nowrap' }} variant="labelSmall" color={textColor}>
          {t('buyMore.cta')}
        </Txt>
      </Button>
      {dialogType && (
        <Suspense
          fallback={!localeFetched && dialogType ? <LoadingModal isOpen={true} handleClose={resetDialogType} /> : null}
        >
          <BuyMore />
        </Suspense>
      )}
    </div>
  );
};
